import {
  Checkbox,
  makeStyles,
  styled,
  Paper,
  TextField,
  Chip,
} from '@material-ui/core'
import React from 'react'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import grey from '@material-ui/core/colors/grey'
import { Autocomplete } from '@material-ui/lab'
import { Translate } from 'react-localize-redux'
import PropTypes from 'prop-types'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const useStyles = makeStyles((theme) => ({
  rootDefault: {
    '& .MuiInputBase-root': {
      minHeight: '40px',
      height: 'auto',
    },
  },
  rootWithMaxTags: {
    '& .MuiInputBase-root': {
      minHeight: '40px',
      paddingTop: '4px',
      paddingBottom: '4px',
      alignItems: 'center',
    },
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'nowrap',
    },
    '& .MuiChip-root': {
      height: 24,
      margin: theme.spacing(0.25),
      fontSize: '0.75rem',
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: '100%',
    },
    '& .MuiInputBase-input': {
      padding: '0px 6px !important',
      marginTop: '2px',
    },
  },
  option: {
    padding: '1px',
    maxHeight: '35px',
  },
}))

const MenuPaper = styled(Paper)({
  border: `1px solid ${grey[300]}`,
  boxShadow: `1px 1px 12px 0 ${grey[400]}`,
})

const MultiSelect = ({
  options,
  label,
  handleSelection,
  selectedValues,
  error,
  helperText,
  maxVisibleTags,
  useTranslationKeys,
  required,
  ...rest
}) => {
  const classes = useStyles()

  const rootClass = maxVisibleTags
    ? classes.rootWithMaxTags
    : classes.rootDefault

  return (
    <Translate>
      {({ translate }) => (
        <Autocomplete
          multiple
          className={rootClass}
          options={options}
          disableCloseOnSelect
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionLabel={(option) =>
            useTranslationKeys ? translate(option.translationKey) : option.label
          }
          value={selectedValues}
          onChange={(event, newValue) => handleSelection(newValue)}
          PaperComponent={MenuPaper}
          classes={{
            option: classes.option,
          }}
          renderTags={(tagValue, getTagProps) => {
            if (maxVisibleTags == null || tagValue.length <= maxVisibleTags) {
              return tagValue.map((option, index) => (
                <Chip
                  key={option.id}
                  label={
                    useTranslationKeys ? (
                      <Translate id={option.translationKey} />
                    ) : (
                      option.label
                    )
                  }
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            const visibleItems = tagValue.slice(0, maxVisibleTags)
            const hiddenItemCount = tagValue.length - maxVisibleTags

            return [
              ...visibleItems.map((option, index) => (
                <Chip
                  key={option.id}
                  label={
                    useTranslationKeys ? (
                      <Translate id={option.translationKey} />
                    ) : (
                      option.label
                    )
                  }
                  size="small"
                  {...getTagProps({ index })}
                />
              )),
              <Chip key="more" label={`+${hiddenItemCount}`} size="small" />,
            ]
          }}
          renderOption={(option, { selected }) => (
            <>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={Boolean(selected)}
              />
              {useTranslationKeys ? (
                <Translate id={option.translationKey} />
              ) : (
                option.label
              )}
            </>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              error={error}
              variant="outlined"
              label={label}
              margin="dense"
              helperText={helperText}
              required={required}
            />
          )}
          {...rest}
        />
      )}
    </Translate>
  )
}

MultiSelect.defaultProps = {
  error: false,
  helperText: '',
  maxVisibleTags: null,
  useTranslationKeys: false,
  required: false,
}

MultiSelect.propTypes = {
  handleSelection: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  label: PropTypes.shape(
     PropTypes.string.isRequired,
  ).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  maxVisibleTags: PropTypes.number,
  useTranslationKeys: PropTypes.bool,
  required: PropTypes.bool,
}

export default MultiSelect
